import { z } from "zod";
import {
  IAbsenceTypeConfigurationOptions,
  EPatternUnit,
  ICustomFieldsConfiguration,
  FieldType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

const EmployeeBase = z.object({
  employeeId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  presentAtQueriedTimeRange: z.boolean().optional().nullable(),
});

const Approver = EmployeeBase;

const Replacer = EmployeeBase.refine(
  (data) => data.presentAtQueriedTimeRange !== false,
  {
    message:
      "Wskazana osoba jest nieobecna w terminie składanego wniosku. Wybierz inną osobę zastępującą.",
    path: ["presentAtQueriedTimeRange"],
  }
);

interface IAbsenceRequestDynamicSchemaOptions {
  config: IAbsenceTypeConfigurationOptions;
  approverRequired: boolean;
  absences: Date[];
  customFieldsConfiguration: ICustomFieldsConfiguration | null;
}

export const createAbsenceRequestDynamicSchema = ({
  config,
  approverRequired,
  absences,
  customFieldsConfiguration,
}: IAbsenceRequestDynamicSchemaOptions) => {
  const { customFields } = customFieldsConfiguration || {};
  const absenceRequestBaseSchema = {
    justification: config.justificationRequired
      ? z
          .string({ message: "Uzasadnienie jest wymagane." })
          .min(1, "Uzasadnienie jest wymagane.")
          .max(255, "Uzasadnienie jest za długie, maksymalnie 255 znaków.")
      : z
          .string()
          .max(255, "Uzasadnienie jest za długie, maksymalnie 255 znaków.")
          .optional(),
    comment: z
      .string()
      .max(255, "Komentarz jest za długi, maksymalnie 255 znaków.")
      .optional()
      .nullable(),
    approver: approverRequired
      ? Approver.nullish().refine((val) => val !== null && val !== undefined, {
          message: "Wskazanie osoby akceptującej jest wymagane.",
        })
      : Approver.optional().nullable(),
    replacer: config.replacerRequired
      ? Replacer.nullish().refine((val) => val !== null && val !== undefined, {
          message: "Wskazanie osoby zastępującej jest wymagane.",
        })
      : Replacer.optional().nullable(),

    attachments: config.attachmentRequired
      ? z
          .array(z.object({ id: z.string() }))
          .nonempty({ message: "Dodanie załącznika jest wymagane." })
      : z.array(z.object({ id: z.string() })).optional(),
  };

  if (customFields && customFields.length > 0) {
    customFields.forEach((field) => {
      const { customFieldId } = field;
      const fieldName = `customField_${customFieldId}`;

      let fieldSchema;

      switch (field.fieldType) {
        case FieldType.TEXT:
          fieldSchema = z
            .string()
            .min(1, "Pole jest wymagane.")
            .max(255, "Wpisana wartość jest za długa, maksymalnie 255 znaków.");
          break;
        case FieldType.CHECKBOX:
          fieldSchema = field.required
            ? z
                .boolean()
                .refine((value) => value === true, "Pole jest wymagane.")
            : z.boolean();
          break;
        case FieldType.DATE:
          fieldSchema = field.required
            ? z.date({ message: "Pole jest wymagane." })
            : z.date().optional();
          break;
        case FieldType.CHOICE_SELECTION:
          if (field.multipleChoice) {
            fieldSchema = z
              .array(z.string())
              .min(1, "Wybierz przynajmniej jedną opcję.");
          } else {
            fieldSchema = z.string().min(1, "Pole jest wymagane.");
          }
          break;
        default:
          fieldSchema = z.string().min(1, "Pole jest wymagane.");
      }

      if (!field.required) {
        fieldSchema = fieldSchema.nullable().optional();
      }

      // @ts-expect-error zod causes TypeScript error
      absenceRequestBaseSchema[fieldName] = fieldSchema;
    });
  }

  const absenceRequestSchemaDay = z.object({
    unit: z.literal(EPatternUnit.Day),
    date: z.date().optional().nullable().default(null),
    startTime: z.number().optional().default(0),
    endTime: z.number().optional().default(0),
    dates: z.tuple([
      z.date({ message: "Data jest wymagana." }),
      z.union([z.date(), z.null()]),
    ]),
    ...absenceRequestBaseSchema,
  });

  const absenceRequestSchemaHour = z.object({
    unit: z.literal(EPatternUnit.Hour),
    date: z.date({ message: "Data jest wymagana." }),
    startTime: z.number(),
    endTime: z.number(),
    dates: z
      .tuple([
        z.union([z.date({ message: "Data jest wymagana." }), z.null()]),
        z.union([z.date(), z.null()]),
      ])
      .optional()
      .default([null, null]),
    ...absenceRequestBaseSchema,
  });

  return z
    .discriminatedUnion("unit", [
      absenceRequestSchemaDay,
      absenceRequestSchemaHour,
    ])
    .refine((data) => data.startTime <= data.endTime, {
      path: ["startTime"],
      message:
        "Godzina rozpoczęcia nie może być późniejsza niż godzina zakończenia.",
    })
    .superRefine((data, ctx) => {
      const [startDate, endDate] = data.dates;

      if (!startDate || !endDate) return;

      const overlappingDates = absences.filter(
        (date) => date >= startDate && date <= endDate
      );

      if (overlappingDates.length > 0) {
        ctx.issues.push({
          code: "custom",
          input: ctx.value,
          message: "W danym zakresie zostały już złożone wnioski.",
          path: ["dates"],
        });
      }
    });
};
